<template>
  <div class="sidebar-pro">
    <div class="sidebar-pro-header">
      <div class="sidebar-pro-icon bus">
        <i class="fa-solid fa-bus"></i>
      </div>
      <div>
        <div class="sidebar-pro-title">{{ $t('dashboard.buses') }}</div>
        <div class="sidebar-pro-subtitle">
          <span class="subtitle-label">{{ $t('dashboard.total') }}:</span>
          <span class="subtitle-value">{{ item.value }}</span>
        </div>
      </div>
    </div>
    <div class="sidebar-pro-table-card">
      <DataTable
        :value="busesList"
        v-if="!loading && busesList.length"
        class="pro-table"
        :scrollable="true"
        scrollHeight="350px"
        paginator
        :rows="8"
        :rowsPerPageOptions="[8, 15, 30, 50]"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        :currentPageReportTemplate="currentPageReportTemplate"
      >
        <Column field="id" :header="$t('table.id')" style="width: 60px; text-align: center" />
        <Column field="plateNumber" :header="$t('dashboard.plateNumber')">
          <template #body="slotProps">
            <span class="bus-plate-cell">
              <i class="fa-solid fa-bus bus-dot"></i>
              {{ slotProps.data.plateNumber }}
            </span>
          </template>
        </Column>
        <Column
          field="busType"
          :header="$t('dashboard.type')"
          style="width: 120px; text-align: center"
        />
        <Column :header="$t('bookingDetails.status')" style="width: 120px; text-align: center">
          <template #body="slotProps">
            <span
              class="status-badge"
              :class="slotProps.data.active_f ? 'active' : 'inactive'"
            >
              <i
                :class="
                  slotProps.data.active_f
                    ? 'fa-solid fa-circle-check'
                    : 'fa-regular fa-circle-xmark'
                "
                class="status-icon"
              ></i>
              {{ slotProps.data.active_f ? $t('status.active') : $t('status.inactive') }}
            </span>
          </template>
        </Column>
      </DataTable>
      <div v-else-if="loading" class="pro-loading">
        <i class="fa-solid fa-spinner fa-spin"></i> {{ $t('dashboard.loading') }}
      </div>
      <div v-else class="pro-empty">
        <i class="fa-regular fa-folder-open"></i> {{ $t('dashboard.noBusesFound') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import DataTable from "primevue/datatable";
import Column from "primevue/column";

const props = defineProps({ item: Object });
const { t } = useI18n();
const store = useStore();
const loading = ref(false);
const busesList = computed(() => store.getters["dashboard/getBusesList"] || []);

const currentPageReportTemplate = computed(() => {
  return `{first} - {last} ${t('table.of')} {totalRecords} ${t('dashboard.buses')}`;
});

async function fetchBuses() {
  loading.value = true;
  await store.dispatch("dashboard/fetchCurrentCorporateBusesList");
  loading.value = false;
}

onMounted(fetchBuses);
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&display=swap");
.sidebar-pro {
  font-family: "Inter", Arial, sans-serif;
  padding: 2.2rem 2.2rem 1.5rem 2.2rem;
  background: #f6f7fb;
  min-height: 100%;
  border-radius: 1.3rem;
  box-shadow: 0 6px 32px 0 rgba(44, 62, 80, 0.1);
}
.sidebar-pro-header {
  display: flex;
  align-items: center;
  gap: 1.7rem;
  margin-bottom: 2.2rem;
}
.sidebar-pro-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #6366f1 60%, #e0e7ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  color: #fff;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.13);
}
.sidebar-pro-icon.bus {
  background: linear-gradient(135deg, #6366f1 60%, #e0e7ff 100%);
}
.sidebar-pro-title {
  font-size: 1.7rem;
  font-weight: 800;
  color: #232946;
  margin-bottom: 0.18rem;
  letter-spacing: 0.5px;
}
.sidebar-pro-subtitle {
  font-size: 1.13rem;
  color: #6b7280;
}
.subtitle-label {
  font-weight: 500;
}
.subtitle-value {
  color: #6366f1;
  font-weight: bold;
  margin-left: 0.3rem;
  font-size: 1.18rem;
}
.sidebar-pro-table-card {
  background: #fff;
  border-radius: 1.1rem;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 1.3rem 1.1rem 1.1rem 1.1rem;
}
.pro-table {
  width: 100%;
  font-size: 1.09rem;
}
:deep(.pro-table .p-datatable-thead > tr > th) {
  background: #f1f2f6 !important;
  color: #232946 !important;
  font-weight: 700 !important;
  border: none !important;
  font-size: 1.07rem;
  letter-spacing: 0.2px;
}
:deep(.pro-table .p-datatable-tbody > tr > td) {
  border-bottom: 1px solid #f3f4f6 !important;
  vertical-align: middle;
}
.bus-plate-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #232946;
}
.bus-dot {
  color: #6366f1;
  font-size: 1.1em;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.25em 1em;
  border-radius: 1em;
  font-size: 0.97em;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: 1.5px solid transparent;
  transition: background 0.2s, color 0.2s;
}
.status-badge.active {
  background: #e0fbe0;
  color: #16a34a;
  border-color: #bbf7d0;
}
.status-badge.inactive {
  background: #fbe0e0;
  color: #ef4444;
  border-color: #fecaca;
}
.status-icon {
  font-size: 1.1em;
}
.pro-loading,
.pro-empty {
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 2.5rem 0;
}
.pro-loading i,
.pro-empty i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  display: block;
}
</style>
